import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import toast, { Toaster } from "react-hot-toast";
import {
  FaHeart,
  FaChevronRight,
  FaChevronLeft,
  FaGlobe,
  FaUpload,
  FaExclamationCircle,
  FaCheck,
  FaChevronDown,
  FaSearch,
  FaSun,
  FaMoon,
  FaUser,
  FaHome,
  FaBriefcase,
  FaGraduationCap,
  FaStar,
  FaVenusMars,
  FaPhone,
  FaEnvelope,
  FaLock,
  FaCalendar,
  FaMapMarker,
  FaClock,
  FaCamera,
  FaFileAlt,
  FaUtensils,
  FaMoneyBillWave,
  FaIdCard,
  FaLinkedin,
  FaInstagram,
  FaWhatsapp,
} from "react-icons/fa";
import { FaEye, FaEyeSlash } from "react-icons/fa";

import api from "../services/api";

const CustomSelect = ({
  options = [],
  value,
  onChange,
  name,
  placeholder,
  disabled = false,
  icon: Icon,
  searchable = false,
  multiple = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredOptions = options.filter((opt) =>
    opt.label.toString().toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedLabel = multiple
    ? (Array.isArray(value) && value.length > 0
      ? value.map((v) => options.find((opt) => opt.value === v)?.label || v).join(", ")
      : placeholder)
    : options?.find((opt) => opt.value === value)?.label || placeholder;

  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) setSearchTerm("");
        }}
        className={`w-full p-3 border border-rose-200 dark:border-glass-border rounded-xl focus:ring-1 focus:ring-primary-pink outline-none bg-[rgba(255,255,255,0.03)] shadow-inner text-left flex items-center justify-between text-sm ${disabled ? "opacity-60 cursor-not-allowed" : "hover:bg-[rgba(255,255,255,0.05)]"
          }`}
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="text-text-muted text-sm" />}
          <span className={value ? "text-gray-900 dark:text-white" : "text-text-muted"}>
            {selectedLabel}
          </span>
        </div>
        <FaChevronDown
          className={`text-text-muted transition-transform text-xs ${isOpen ? "rotate-180" : ""
            }`}
        />
      </button>
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow-xl flex flex-col max-h-64">
          {searchable && (
            <div className="p-2 border-b border-gray-100 dark:border-gray-800 shrink-0">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-full pl-8 pr-3 py-2 bg-[rgba(0,0,0,0.05)] dark:bg-[rgba(255,255,255,0.05)] border-none rounded-lg text-sm text-gray-900 dark:text-white focus:ring-1 focus:ring-primary-pink outline-none"
                  autoFocus
                />
              </div>
            </div>
          )}
          <div className="overflow-y-auto custom-scrollbar flex-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const isSelected = multiple
                  ? Array.isArray(value) && value.includes(opt.value)
                  : value === opt.value;
                return (
                  <div
                    key={opt.value}
                    onClick={() => {
                      if (multiple) {
                        const currentValues = Array.isArray(value) ? value : [];
                        let newValues;
                        if (currentValues.includes(opt.value)) {
                          newValues = currentValues.filter((v) => v !== opt.value);
                        } else {
                          newValues = [...currentValues, opt.value];
                        }
                        onChange({ target: { name, value: newValues } });
                      } else {
                        onChange({ target: { name, value: opt.value } });
                        setIsOpen(false);
                        setSearchTerm("");
                      }
                    }}
                    className={`px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-rose-50 dark:hover:bg-gray-800 transition-colors text-sm ${isSelected && !multiple
                      ? "bg-rose-50 dark:bg-gray-800 text-rose-600 font-medium"
                      : "text-gray-700 dark:text-gray-200"
                      }`}
                  >
                    {multiple && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        className="w-4 h-4 text-primary-pink rounded border-gray-300 focus:ring-primary-pink dark:border-gray-600 dark:bg-gray-700"
                      />
                    )}
                    {opt.icon && <opt.icon className="text-sm text-gray-400" />}
                    <span className="flex-1">{opt.label}</span>
                    {!multiple && isSelected && (
                      <FaCheck className="text-rose-600 text-xs" />
                    )}
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No options found
              </div>
            )}
          </div>
        </div>
      )}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsOpen(false);
            setSearchTerm("");
          }}
        ></div>
      )}
    </div>
  );
};

const Register = () => {
  const [step, setStep] = useState(1);
  const [lang, setLang] = useState("en");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMasterData, setLoadingMasterData] = useState(true);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const fieldRefs = useRef({});

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return false; // Default to light mode
  });

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
      document.documentElement.classList.remove("light");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      document.documentElement.classList.add("light");
      localStorage.setItem("theme", "light");
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const passwordRegex =
    /^[A-Z](?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{5,}$/;

  const [masterData, setMasterData] = useState({
    religions: [],
    castes: {},
    subcastes: {},
    stars: [],
    rasis: [],
    education: [],
    occupations: [],
  });

  const [formData, setFormData] = useState({
    // Basic Details
    profileFor: "",
    fullName: "",
    gender: "",
    dob: "",
    birthTime: "",
    birthPlace: "",

    // Account Details
    mobile: "",
    whatsappNumber: "", // Added WhatsApp number field
    email: "",
    password: "",
    confirmPassword: "",
    role: "user",

    // Physical Attributes
    heightFeet: "",
    heightInches: "",
    weight: "",
    bodyType: "",
    complexion: "",

    // Family Background
    fatherStatus: "Alive",
    fatherOccupation: "",
    motherStatus: "Alive",
    motherOccupation: "",
    noOfBrothers: "",
    noOfSisters: "",
    familyType: "",
    familyValues: "",
    familyOrigin: "",

    // Community Details
    maritalStatus: "",
    childrenLivingWith: "",
    childrenNotLivingWith: "",
    religion: "",
    caste: "",
    subcaste: "",
    gothram: "",

    // Horoscope Details
    star: "",
    pada: "",
    rasi: "",
    nadi: "",
    manglik: "",
    dosham: "",
    doshamTypes: [],

    // Personal Habits
    diet: "",
    smoking: "",
    drinking: "",

    // Education & Career
    education: "",
    educationDetails: "",
    university: "",
    occupation: "",
    company: "",
    designation: "",
    income: "",

    // Location
    currentCity: "",
    state: "Tamil Nadu",
    pincode: "",
    country: "India",

    // Uploads
    profilePhoto: null,
    jathagamPhoto: null,
    idProof: null,
    horoscopePdf: null,

    // Social Media
    linkedin: "",
    instagram: "",

    // Privacy Settings
    showMobile: true,
    showEmail: true,
    visibleTo: "All",
  });

  const t = {
    en: {
      title: "Begin Your Journey to Forever",
      step1Title: "Personal Information",
      step1Sub: "Tell us about yourself",
      step2Title: "Family & Background",
      step2Sub: "Share your family and community details",
      step3Title: "Horoscope & Lifestyle",
      step3Sub: "Astrological and personal preferences",
      step4Title: "Career & Final Details",
      step4Sub: "Complete your profile",
      requiredNote: "* Required fields",

      // Sections
      basicInfo: "Basic Information",
      physicalAttributes: "Physical Attributes",
      familyInfo: "Family Information",
      communityInfo: "Community Details",
      horoscopeInfo: "Horoscope Details",
      lifestyle: "Lifestyle & Habits",
      educationCareer: "Education & Career",
      locationInfo: "Location Details",
      uploads: "Documents & Photos",
      socialMedia: "Social Media (Optional)",

      // Labels
      profileFor: "Profile Created For",
      name: "Full Name ",
      gender: "Gender ",
      dob: "Date of Birth ",
      birthTime: "Birth Time ",
      birthPlace: "Birth Place ",
      mobile: "Mobile Number ",
      whatsappNumber: "WhatsApp Number", // Added WhatsApp label
      email: "Email Address",
      password: "Create Password ",
      confirmPassword: "Confirm Password ",

      height: "Height",
      weight: "Weight (kg)",
      bodyType: "Body Type",
      complexion: "Complexion",

      fatherStatus: "Father's Status",
      fatherOccupation: "Father's Occupation",
      motherStatus: "Mother's Status",
      motherOccupation: "Mother's Occupation",
      siblings: "Siblings",
      brothers: "Brothers",
      sisters: "Sisters",
      familyType: "Family Type",
      familyValues: "Family Values",
      familyOrigin: "Family Origin",

      maritalStatus: "Marital Status",
      childrenDetails: "Children Details",
      livingWith: "Living with you",
      notLivingWith: "Not living with you",
      religion: "Religion",
      caste: "Caste",
      subcaste: "Subcaste",
      gothram: "Gothram",

      star: "Star (Nakshatra)",
      pada: "Pada",
      rasi: "Rasi (Moon Sign)",
      nadi: "Nadi",
      manglik: "Manglik",
      dosham: "Dosham",

      diet: "Diet",
      smoking: "Smoking",
      drinking: "Drinking",

      education: "Highest Education",
      educationDetails: "Specialization",
      university: "University/College",
      occupation: "Occupation",
      company: "Company",
      designation: "Designation",
      income: "Annual Income",

      currentCity: "Current City",
      pincode: "Pincode",
      state: "State",
      country: "Country",

      profilePhoto: "Profile Photo",
      jathagamPhoto: "Jathagam/ Horoscope",
      idProof: "ID Proof",
      horoscopePdf: "Horoscope PDF",

      linkedin: "LinkedIn Profile",
      instagram: "Instagram Profile",

      continue: "Continue",
      back: "Back",
      createProfile: "Complete Registration",
      required: "This field is required",
      passMismatch: "Passwords do not match",
      invalidMobile: "Enter a valid 10-digit mobile number",
      invalidWhatsApp: "Enter a valid 10-digit WhatsApp number",
      invalidEmail: "Enter a valid email address",
      passwordRule:
        "Password must start with a capital letter, be at least 6 characters, and include lowercase, number & special character",

      terms: "By continuing, you agree to our Terms & Privacy Policy",
    },
    ta: {
      title: "நிரந்தர பயணத்தை தொடங்குங்கள்",
      step1Title: "தனிப்பட்ட தகவல்கள்",
      step1Sub: "உங்களைப் பற்றி எங்களிடம் சொல்லுங்கள்",
      step2Title: "குடும்பம் & பின்னணி",
      step2Sub: "உங்கள் குடும்ப மற்றும் சமூக விவரங்களைப் பகிரவும்",
      step3Title: "ஜாதகம் & வாழ்க்கை முறை",
      step3Sub: "ஜோதிட மற்றும் தனிப்பட்ட விருப்பங்கள்",
      step4Title: "தொழில் & இறுதி விவரங்கள்",
      step4Sub: "உங்கள் சுயவிவரத்தை நிறைவு செய்யுங்கள்",
      requiredNote: "* கட்டாய புலங்கள்",

      basicInfo: "அடிப்படை தகவல்கள்",
      physicalAttributes: "உடல் கூறுகள்",
      familyInfo: "குடும்ப தகவல்கள்",
      communityInfo: "சமூக விவரங்கள்",
      horoscopeInfo: "ஜாதக விவரங்கள்",
      lifestyle: "வாழ்க்கை முறை & பழக்கங்கள்",
      educationCareer: "கல்வி & தொழில்",
      locationInfo: "இருப்பிட விவரங்கள்",
      uploads: "ஆவணங்கள் & புகைப்படங்கள்",
      socialMedia: "சமூக ஊடகம் (விருப்பம்)",

      profileFor: "யாருக்காக பதிவு செய்கிறீர்கள் *",
      name: "முழு பெயர் *",
      gender: "பாலினம் *",
      dob: "பிறந்த தேதி *",
      birthTime: "பிறந்த நேரம் *",
      birthPlace: "பிறந்த இடம் *",
      mobile: "மொபைல் எண் *",
      whatsappNumber: "WhatsApp எண்", // Added WhatsApp label in Tamil
      email: "மின்னஞ்சல் முகவரி",
      password: "கடவுச்சொல் உருவாக்கு *",
      confirmPassword: "கடவுச்சொல்லை உறுதிப்படுத்து *",

      height: "உயரம்",
      weight: "எடை (கிலோ)",
      bodyType: "உடல் வகை",
      complexion: "நிறம்",

      fatherStatus: "தந்தையின் நிலை",
      fatherOccupation: "தந்தையின் தொழில்",
      motherStatus: "தாயின் நிலை",
      motherOccupation: "தாயின் தொழில்",
      siblings: "சகோதரர்கள்",
      brothers: "சகோதரர்கள்",
      sisters: "சகோதரிகள்",
      familyType: "குடும்ப வகை",
      familyValues: "குடும்ப மதிப்புகள்",
      familyOrigin: "குடும்ப தோற்றம்",

      maritalStatus: "திருமண நிலை *",
      childrenDetails: "குழந்தைகள் விவரங்கள்",
      livingWith: "உங்களுடன் வசிப்பவர்கள்",
      notLivingWith: "உங்களுடன் வசிக்காதவர்கள்",
      religion: "மதம் *",
      caste: "சாதி *",
      subcaste: "உட்பிரிவு",
      gothram: "கோத்திரம்",

      star: "நட்சத்திரம் *",
      pada: "பாதம்",
      rasi: "ராசி *",
      nadi: "நாடி",
      manglik: "மாங்கல்யம்",
      dosham: "தோஷம்",

      diet: "உணவு பழக்கம்",
      smoking: "புகைப்பழக்கம்",
      drinking: "குடிப்பழக்கம்",

      education: "கல்வி தகுதி *",
      educationDetails: "சிறப்புப் பாடம்",
      university: "பல்கலைக்கழகம் / கல்லூரி",
      occupation: "தொழில்",
      company: "நிறுவனம்",
      designation: "பதவி",
      income: "ஆண்டு வருமானம்",

      currentCity: "தற்போதைய நகரம் *",
      pincode: "அஞ்சல் குறியீடு",
      state: "மாநிலம்",
      country: "நாடு",

      profilePhoto: "சுயவிவர புகைப்படம் *",
      jathagamPhoto: "ஜாதக புகைப்படம்",
      idProof: "அடையாள சான்று",
      horoscopePdf: "ஜாதக PDF",

      linkedin: "LinkedIn சுயவிவரம்",
      instagram: "Instagram சுயவிவரம்",

      privacySettings: "தனியுரிமை அமைப்புகள்",
      showMobile: "பொருந்துபவர்களுக்கு மொபைல் காட்டு",
      showEmail: "பொருந்துபவர்களுக்கு மின்னஞ்சல் காட்டு",
      visibleTo: "சுயவிவரம் காண்பிக்கப்படும்",

      continue: "தொடரவும்",
      back: "பின்செல்ல",
      createProfile: "பதிவை முடிக்கவும்",
      required: "இந்த புலம் தேவை",
      passMismatch: "கடவுச்சொற்கள் பொருந்தவில்லை",
      invalidMobile: "சரியான 10-இலக்க மொபைல் எண்ணை உள்ளிடவும்",
      invalidWhatsApp: "சரியான 10-இலக்க WhatsApp எண்ணை உள்ளிடவும்",
      invalidEmail: "சரியான மின்னஞ்சல் முகவரியை உள்ளிடவும்",
      passwordRule:
        "கடவுச்சொல் பெரிய எழுத்தில் தொடங்கி, குறைந்தது 6 எழுத்துகள், எண் மற்றும் சிறப்பு குறியீடு கொண்டிருக்க வேண்டும்",

      terms:
        "தொடர்வதன் மூலம், எங்கள் விதிமுறைகள் & தனியுரிமைக் கொள்கையை ஏற்கிறீர்கள்",
    },

  };

  const txt = t[lang];

  // Enhanced Options
  const profileForOptions = [
    { value: "Self", label: lang === "en" ? "Self" : "தனக்காக", icon: FaUser },
    { value: "Son", label: lang === "en" ? "Son" : "மகன்", icon: FaUser },
    {
      value: "Daughter",
      label: lang === "en" ? "Daughter" : "மகள்",
      icon: FaUser,
    },
    {
      value: "Brother",
      label: lang === "en" ? "Brother" : "சகோதரன்",
      icon: FaUser,
    },
    {
      value: "Sister",
      label: lang === "en" ? "Sister" : "சகோதரி",
      icon: FaUser,
    },
    {
      value: "Friend",
      label: lang === "en" ? "Friend" : "நண்பர்",
      icon: FaUser,
    },
  ];

  const genderOptions = [
    { value: "Male", label: lang === "en" ? "Male" : "ஆண்" },
    { value: "Female", label: lang === "en" ? "Female" : "பெண்" },
  ];

  const bodyTypeOptions = [
    { value: "Slim", label: "Slim" },
    { value: "Average", label: "Average" },
    { value: "Athletic", label: "Athletic" },
    { value: "Heavy", label: "Heavy" },
  ];

  const complexionOptions = [
    { value: "Very Fair", label: "Very Fair" },
    { value: "Fair", label: "Fair" },
    { value: "Wheatish", label: "Wheatish" },
    { value: "Dark", label: "Dark" },
  ];

  const familyTypeOptions = [
    { value: "Nuclear", label: "Nuclear" },
    { value: "Joint", label: "Joint" },
    { value: "Extended", label: "Extended" },
  ];

  const familyValuesOptions = [
    { value: "Traditional", label: "Traditional" },
    { value: "Moderate", label: "Moderate" },
    { value: "Liberal", label: "Liberal" },
  ];
  const padaOptions = [
    { label: "Not Specified", value: "" },
    { label: "1", value: "1" },
    { label: "2", value: "2" },
    { label: "3", value: "3" },
    { label: "4", value: "4" },
  ];
  const maritalOptions = [
    {
      value: "Never Married",
      label: lang === "en" ? "Never Married" : "திருமணமாகாதவர்",
    },
    { value: "Widowed", label: lang === "en" ? "Widowed" : "விதவை/விதவர்" },
    { value: "Divorced", label: lang === "en" ? "Divorced" : "விவாகரத்து" },
    { value: "Separated", label: lang === "en" ? "Separated" : "பிரிந்தவர்" },
    {
      value: "Awaiting Divorce",
      label: lang === "en" ? "Awaiting Divorce" : "விவாகரத்து நிலுவை",
    },
  ];


  useEffect(() => {
    const fetchMasterData = async () => {
      setLoadingMasterData(true);
      try {
        const res = await api.get("/master-data");
        setMasterData(res.data.data); // ✅ IMPORTANT FIX
      } catch (err) {
        console.error("Failed to fetch master data:", err);
      } finally {
        setLoadingMasterData(false);
      }
    };
    fetchMasterData();
  }, []);



  // Options
  const religionOptions = (masterData.religions || []).map(r => ({ value: r, label: r }));

  const casteOptions = formData.religion
    ? (masterData.castes[formData.religion] || []).map(c => ({ value: c, label: c }))
    : [];

  // Fixed: subcastes depend on religion, not caste
  // ✅ FIXED
  const subcasteOptions = formData.religion
    ? (masterData.subcastes[formData.religion] || []).map(sc => ({
      value: sc,
      label: sc
    }))
    : [];


  const educationOptions = (masterData.education || []).map(ed => ({
    value: ed,
    label: ed
  }));


  const occupationOptions = (masterData.occupations || []).map(occ => ({
    value: occ,
    label: occ
  }));


  const baseStarOptions = [
    "அஸ்வினி (Ashwini)",
    "பரணி (Bharani)",
    "கிருத்திகை (Krittika)",
    "ரோகிணி (Rohini)",
    "மிருகசீரிஷம் (Mrigashira)",
    "திருவாதிரை (Ardra)",
    "புனர்பூசம் (Punarvasu)",
    "பூசம் (Pushya)",
    "ஆயில்யம் (Ashlesha)",
    "மகம் (Magha)",
    "பூரம் (Purva Phalguni)",
    "உத்திரம் (Uttara Phalguni)",
    "ஹஸ்தம் (Hasta)",
    "சித்திரை (Chitra)",
    "சுவாதி (Swati)",
    "விசாகம் (Vishakha)",
    "அனுஷம் (Anuradha)",
    "கேட்டை (Jyeshtha)",
    "மூலம் (Mula)",
    "பூராடம் (Purva Ashadha)",
    "உத்திராடம் (Uttara Ashadha)",
    "திருவோணம் (Shravana)",
    "அவிட்டம் (Dhanishta)",
    "சதயம் (Shatabhisha)",
    "பூரட்டாதி (Purva Bhadrapada)",
    "உத்திரட்டாதி (Uttara Bhadrapada)",
    "ரேவதி (Revati)",
  ];

  const starOptions = ["Muslim", "Christian"].includes(formData.religion)
    ? [{ value: "Not Applicable", label: "Not Applicable" }]
    : baseStarOptions.map((star) => ({ value: star, label: star }));

  const baseRasiOptions = [
    "Aries - மேஷம்",
    "Taurus - ரிஷபம்",
    "Gemini - மிதுனம்",
    "Cancer - கடகம்",
    "Leo - சிம்மம்",
    "Virgo - கன்னி",
    "Libra - துலாம்",
    "Scorpio - விருச்சிகம்",
    "Sagittarius - தனுசு",
    "Capricorn - மகரம்",
    "Aquarius - கும்பம்",
    "Pisces - மீனம்",
  ];

  const rasiOptions = ["Muslim", "Christian"].includes(formData.religion)
    ? [{ value: "Not Applicable", label: "Not Applicable" }]
    : baseRasiOptions.map((rasi) => ({ value: rasi, label: rasi }));

  const nadiOptions = [
    { label: "Not Specified", value: "" },
    { value: "Aadi", label: "Aadi" },
    { value: "Madhya", label: "Madhya" },
    { value: "Antya", label: "Antya" },
  ];

  const dietOptions = [
    { value: "Vegetarian", label: "Vegetarian" },
    { value: "Non-Vegetarian", label: "Non-Vegetarian" },
    { value: "Eggetarian", label: "Eggetarian" },
    { value: "Vegan", label: "Vegan" },
  ];

  const habitOptions = [
    { value: "No", label: "No" },
    { value: "Occasionally", label: "Occasionally" },
    { value: "Yes", label: "Yes" },
  ];


  const incomeOptions = [
    { value: "0-2 Lakhs", label: "0-2 Lakhs" },
    { value: "2-5 Lakhs", label: "2-5 Lakhs" },
    { value: "5-10 Lakhs", label: "5-10 Lakhs" },
    { value: "10-20 Lakhs", label: "10-20 Lakhs" },
    { value: "20+ Lakhs", label: "20+ Lakhs" },
  ];

  const visibleToOptions = [
    { value: "All", label: "All Registered Users" },
    { value: "MyCommunity", label: "My Community Only" },
    { value: "MatchesOnly", label: "My Matches Only" },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    setFormData(prev => {
      if (name === "religion") {
        const isNotApplicable = ["Muslim", "Christian"].includes(value);
        return {
          ...prev,
          religion: value,
          caste: "",
          subcaste: "",
          star: isNotApplicable ? "Not Applicable" : (prev.star === "Not Applicable" ? "" : prev.star),
          rasi: isNotApplicable ? "Not Applicable" : (prev.rasi === "Not Applicable" ? "" : prev.rasi)
        };
      }

      if (name === "caste") {
        return {
          ...prev,
          caste: value,
          subcaste: ""
        };
      }

      if (type === "checkbox") {
        return { ...prev, [name]: checked };
      }

      if (type === "file") {
        return { ...prev, [name]: files[0] };
      }

      return { ...prev, [name]: value };
    });

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }

    if (name === "pincode" && value.length === 6) {
      handlePincodeLookup(value);
    }
  };


  const handlePincodeLookup = async (pincode) => {
    try {
      const response = await fetch(
        `https://api.postalpincode.in/pincode/${pincode}`
      );
      const data = await response.json();
      if (data[0].Status === "Success") {
        const postOffice = data[0].PostOffice[0];
        setFormData((prev) => ({
          ...prev,
          currentCity: postOffice.District,
          state: postOffice.State,
        }));
      }
    } catch (error) {
      console.error("Pincode API error:", error);
    }
  };

  const scrollToError = (currentErrors) => {
    const errorSource = currentErrors || errors;
    const firstErrorKey = Object.keys(errorSource)[0];
    if (firstErrorKey && fieldRefs.current[firstErrorKey]) {
      fieldRefs.current[firstErrorKey].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };
  const calculateAge = (dob) => {
    if (!dob) return 0;

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };
  const validateStep = (currentStep) => {
    let newErrors = {};

    // regex helpers
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^[6-9]\d{9}$/;

    if (currentStep === 1) {
      if (!formData.profileFor) newErrors.profileFor = txt.required;
      if (!formData.fullName) newErrors.fullName = txt.required;
      if (!formData.gender) newErrors.gender = txt.required;
      if (!formData.dob) {
        newErrors.dob = txt.required;
      } else if (formData.dob && formData.gender) {
        const age = calculateAge(formData.dob);

        if (formData.gender === "Female" && age < 18) {
          newErrors.dob = "Female must be at least 18 years old";
        }

        if (formData.gender === "Male" && age < 21) {
          newErrors.dob = "Male must be at least 21 years old";
        }
      }

      if (!formData.birthTime || !/^(0[1-9]|1[0-2]):[0-5][0-9] (AM|PM)$/.test(formData.birthTime)) {
        newErrors.birthTime = "Please select complete birth time";
      }
      if (!formData.birthPlace) newErrors.birthPlace = txt.required;
      /* WHATSAPP NUMBER (REQUIRED) */
      if (!formData.whatsappNumber) {
        newErrors.whatsappNumber = txt.required;
      } else if (!mobileRegex.test(formData.whatsappNumber)) {
        newErrors.whatsappNumber = txt.invalidWhatsApp;
      }

      /* MOBILE */
      if (!formData.mobile) {
        newErrors.mobile = txt.required;
      } else if (!mobileRegex.test(formData.mobile)) {
        newErrors.mobile =
          txt.invalidMobile || "Enter a valid 10-digit mobile number";
      }

      /* WHATSAPP NUMBER (REQUIRED) */
      if (!formData.whatsappNumber) {
        newErrors.whatsappNumber = txt.required;
      } else if (!mobileRegex.test(formData.whatsappNumber)) {
        newErrors.whatsappNumber =
          txt.invalidWhatsApp || "Enter a valid 10-digit WhatsApp number";
      }

      /* EMAIL */
      if (!formData.email) {
        newErrors.email = txt.required;
      } else if (!emailRegex.test(formData.email)) {
        newErrors.email = txt.invalidEmail || "Enter a valid email address";
      }

      /* PASSWORD VALIDATION */
      const password = formData.password.trim();

      if (!password) {
        newErrors.password = txt.required;
      } else if (!passwordRegex.test(password)) {
        newErrors.password = txt.passwordRule;
      }


      /* CONFIRM PASSWORD */
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = txt.required;
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = txt.passMismatch;
      }

      if (!formData.profilePhoto)
        newErrors.profilePhoto = "Profile photo is required";
    } else if (currentStep === 2) {
      if (!formData.maritalStatus) newErrors.maritalStatus = txt.required;
      if (!formData.religion) newErrors.religion = txt.required;
      if (!formData.caste) newErrors.caste = txt.required;
    } else if (currentStep === 3) {
      if (!formData.star) newErrors.star = txt.required;
      if (!formData.rasi) newErrors.rasi = txt.required;
      if (!formData.dosham) {
        newErrors.dosham = txt.required;
      } else if (formData.dosham === "Yes" && (!formData.doshamTypes || formData.doshamTypes.length === 0)) {
        newErrors.doshamTypes = lang === "en" ? "Please select at least one dosham type" : "குறைந்தது ஒரு தோஷ வகையையாவது தேர்ந்தெடுக்கவும்";
      }

    } else if (currentStep === 4) {
      if (!formData.education) newErrors.education = txt.required;
      if (!formData.currentCity) newErrors.currentCity = txt.required;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setTimeout(() => scrollToError(newErrors), 100);
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrev = () => {
    setStep(step - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep(step)) return;

    try {
      setLoading(true); //  Start loading

      const payload = new FormData();

      Object.keys(formData).forEach((key) => {
        if (
          key === "profilePhoto" ||
          key === "jathagamPhoto" ||
          key === "idProof" ||
          key === "horoscopePdf"
        ) {
          if (formData[key]) payload.append(key, formData[key]);
        } else {
          if (Array.isArray(formData[key])) {
            payload.append(key, JSON.stringify(formData[key]));
          } else {
            payload.append(key, formData[key] || "");
          }
        }
      });

      payload.set("role", "user");

      const response = await api.post("/register", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      //  SUCCESS TOAST
      toast.success("Registration Successful!", {
        style: {
          borderRadius: "12px",
          background: "linear-gradient(to right, #f43f5e, #ec4899)",
          color: "#fff",
          padding: "16px",
          fontWeight: "600",
        },
        iconTheme: {
          primary: "#ffffff",
          secondary: "#f43f5e",
        },
      });

      const data = response.data;
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      setTimeout(() => {
        navigate("/login");
      }, 1500);

    } catch (error) {
      console.error("Registration error:", error);

      const response = error.response?.data;

      //  If backend sends validation errors object
      if (response?.errors && Object.keys(response.errors).length > 0) {
        setErrors(response.errors);
        setTimeout(scrollToError, 100);
        return;
      }

      // If backend sends single message
      if (response?.message) {
        toast.error(response.message);
        return;
      }

      //  If backend sends plain string
      if (typeof response === "string") {
        toast.error(response);
        return;
      }

      //  Network error
      if (!error.response) {
        toast.error("Network error. Please check your connection.");
        return;
      }

      //  Fallback
      toast.error("Registration failed. Please try again.");
    } finally {
      setLoading(false); // ✅ Stop loading
    }
  };

  const renderError = (field) => {
    return errors[field] ? (
      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
        <FaExclamationCircle className="text-xs" /> {errors[field]}
      </p>
    ) : null;
  };

  const renderSectionHeader = (title, subtitle, IconComponent) => (
    <div className="flex items-center gap-3 mb-6">
      <div className="p-2 bg-[rgba(255,46,136,0.1)] border border-[rgba(255,46,136,0.2)] rounded-xl shadow-[0_0_15px_rgba(255,46,136,0.1)]">
        <IconComponent className="text-primary-pink text-lg" />
      </div>
      <div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white tracking-wide">{title}</h3>
        {subtitle && <p className="text-sm text-text-secondary">{subtitle}</p>}
      </div>
    </div>
  );

  const renderInputWithIcon = (
    IconComponent,
    label,
    name,
    type = "text",
    placeholder = "",
    required = false
  ) => {
    const isPassword = name === "password" || name === "confirmPassword";
    const isVisible =
      name === "password" ? showPassword : showConfirmPassword;

    return (
      <div ref={(el) => (fieldRefs.current[name] = el)}>
        <label className="block text-sm font-medium text-text-secondary mb-2">
          {label} {required && <span className="text-primary-pink">*</span>}
        </label>

        <div className="relative">
          {/* Left icon */}
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">
            <IconComponent className="text-sm" />
          </div>

          <input
            type={isPassword ? (isVisible ? "text" : "password") : (type === "date" && !formData[name] ? "text" : type)}
            onFocus={(e) => { if (type === "date") e.currentTarget.type = "date"; }}
            onBlur={(e) => { if (type === "date" && !e.currentTarget.value) e.currentTarget.type = "text"; }}
            name={name}
            value={formData[name]}
            onChange={handleChange}
            placeholder={placeholder}
            maxLength={
              name === "mobile" || name === "whatsappNumber" ? 10 : undefined
            }
            inputMode={
              name === "mobile" || name === "whatsappNumber"
                ? "numeric"
                : undefined
            }
            pattern={
              name === "mobile" || name === "whatsappNumber"
                ? "[0-9]*"
                : undefined
            }
            className={`w-full pl-10 ${isPassword ? "pr-10" : "pr-4"
              } py-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] rounded-xl
          focus:ring-1 focus:ring-primary-pink focus:border-primary-pink
          outline-none text-sm text-gray-900 dark:text-white placeholder-text-muted shadow-inner`}
          />

          {/* 👁 React Icon Eye */}
          {isPassword && (
            <button
              type="button"
              onClick={() =>
                name === "password"
                  ? setShowPassword((prev) => !prev)
                  : setShowConfirmPassword((prev) => !prev)
              }
              className="absolute right-3 top-1/2 -translate-y-1/2
                       text-text-muted hover:text-primary-pink"
            >
              {isVisible ? (
                <FaEyeSlash className="text-sm" />
              ) : (
                <FaEye className="text-sm" />
              )}
            </button>
          )}
        </div>

        {renderError(name)}
      </div>
    );
  };



  return (
    <div className="min-h-screen bg-dark-navy font-sans relative overflow-x-hidden">
      {/* Ambient Glows */}
      <div className="absolute top-0 left-0 w-full h-[500px] bg-primary-pink/10 blur-[150px] pointer-events-none"></div>

      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
        }}
      />
      {/* Header */}
      <div className="bg-glass-card backdrop-blur-xl border-b border-glass-border py-4 sticky top-0 z-50 shadow-glass">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <div className="p-2 bg-[rgba(255,46,136,0.1)] border border-[rgba(255,46,136,0.2)] rounded-xl shadow-[0_0_15px_rgba(255,46,136,0.2)]">
              <FaHeart className="text-primary-pink text-xl" />
            </div>
            <span className="text-xl font-bold tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300">
              <span className="text-primary-pink">Kalyana Matrimony</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="flex items-center justify-center w-10 h-10 bg-[rgba(255,255,255,0.05)] border border-glass-border rounded-xl hover:bg-black/5 dark:hover:bg-[rgba(255,255,255,0.1)] transition-colors text-gray-900 dark:text-white"
              title="Toggle Theme"
            >
              {isDarkMode ? <FaSun className="text-lg text-yellow-500" /> : <FaMoon className="text-lg text-primary-pink" />}
            </button>
            <button
              onClick={() => setLang(lang === "en" ? "ta" : "en")}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[rgba(255,255,255,0.05)] border border-glass-border hover:bg-black/5 dark:hover:bg-[rgba(255,255,255,0.1)] text-sm font-medium text-gray-900 dark:text-white transition-colors"
            >
              <FaGlobe className="text-primary-pink" /> {lang === "en" ? "தமிழ்" : "English"}
            </button>
            <div className="text-sm text-text-secondary hidden md:block">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-primary-pink font-bold hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Progress Steps - Modern Design */}
          <div className="bg-glass-card backdrop-blur-[20px] border border-rose-200 dark:border-glass-border rounded-3xl shadow-glass p-8 mb-8">
            <div className="text-center mb-10">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 tracking-wide">
                {txt.title}
              </h1>
              <p className="text-text-secondary font-medium tracking-widest uppercase text-xs">Step {step} of 4</p>
            </div>

            <div className="relative">
              <div className="flex justify-between">
                {[
                  { num: 1, title: txt.step1Title, icon: FaUser },
                  { num: 2, title: txt.step2Title, icon: FaHome },
                  { num: 3, title: txt.step3Title, icon: FaStar },
                  { num: 4, title: txt.step4Title, icon: FaBriefcase },
                ].map((item) => (
                  <div
                    key={item.num}
                    className="flex flex-col items-center z-10"
                  >
                    <div
                      className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-500 ${step >= item.num
                        ? "bg-gradient-to-r from-primary-grad-start to-primary-grad-end text-white shadow-[0_0_20px_rgba(255,46,136,0.4)]"
                        : "bg-[rgba(255,255,255,0.05)] text-text-muted border-2 border-glass-border"
                        }`}
                    >
                      {step > item.num ? (
                        <FaCheck className="text-lg" />
                      ) : (
                        <item.icon className="text-lg" />
                      )}
                    </div>
                    <span className="mt-4 text-xs font-bold tracking-wider text-text-secondary uppercase text-center hidden md:block">
                      {item.title}
                    </span>
                    <span className="mt-1 text-xs text-text-muted md:hidden">
                      Step {item.num}
                    </span>
                  </div>
                ))}
              </div>
              <div
                className="absolute top-7 left-0 w-full h-1 bg-[rgba(255,255,255,0.05)] -z-0"
                style={{ left: "12%", width: "76%" }}
              >
                <div
                  className="h-full bg-gradient-to-r from-primary-grad-start to-primary-grad-end transition-all duration-500 rounded-full shadow-[0_0_15px_rgba(255,46,136,0.5)]"
                  style={{ width: `${((step - 1) / 3) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Main Form Container */}
          <div className="bg-glass-card backdrop-blur-[20px] border border-rose-200 dark:border-glass-border rounded-3xl shadow-glass overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-primary-grad-start to-primary-grad-end w-full"></div>

            <form onSubmit={handleSubmit} className="p-8 lg:p-10">
              <div className="text-xs font-bold text-primary-pink tracking-wider uppercase text-right mb-6">
                {txt.requiredNote}
              </div>

              {/* Step 1: Basic Information */}
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-8"
                >
                  {renderSectionHeader(txt.basicInfo, txt.step1Sub, FaUser)}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left Column */}
                    <div className="space-y-6">
                      <div ref={(el) => (fieldRefs.current.profileFor = el)}>
                        <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                          {txt.profileFor} <span className="text-primary-pink">*</span>
                        </label>
                        <CustomSelect
                          options={profileForOptions}
                          value={formData.profileFor}
                          onChange={handleChange}
                          name="profileFor"
                          placeholder="Select who this profile is for"
                          icon={FaUser}
                        />
                        {renderError("profileFor")}
                      </div>

                      {renderInputWithIcon(
                        FaUser,
                        txt.name,
                        "fullName",
                        "text",
                        "Enter your full name",
                        true
                      )}

                      <div ref={(el) => (fieldRefs.current.gender = el)}>
                        <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                          {txt.gender} <span className="text-primary-pink">*</span>
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          {genderOptions.map((option) => (
                            <label
                              key={option.value}
                              className={`p-4 border rounded-xl flex items-center justify-center gap-2 cursor-pointer transition-all ${formData.gender === option.value
                                ? "border-primary-pink bg-[rgba(255,46,136,0.1)] text-primary-pink"
                                : "border-rose-200 dark:border-glass-border hover:bg-[rgba(255,255,255,0.03)]"
                                }`}
                            >
                              <input
                                type="radio"
                                name="gender"
                                value={option.value}
                                checked={formData.gender === option.value}
                                onChange={handleChange}
                                className="hidden"
                              />
                              <FaVenusMars
                                className={
                                  formData.gender === option.value
                                    ? "text-primary-pink"
                                    : "text-text-muted"
                                }
                              />
                              <span className="font-medium">
                                {option.label}
                              </span>
                            </label>
                          ))}
                        </div>
                        {renderError("gender")}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {renderInputWithIcon(
                          FaCalendar,
                          txt.dob,
                          "dob",
                          "date",
                          "dd-mm-yyyy",
                          true
                        )}
                        {/* Custom Birth Time Field */}
                        <div ref={(el) => (fieldRefs.current.birthTime = el)}>
                          <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                            {txt.birthTime} <span className="text-primary-pink">*</span>
                          </label>
                          <div className="flex gap-2 relative">
                            {/* Hour */}
                            <div className="flex-1 relative">
                              <CustomSelect
                                name="birthTimeHour"
                                value={formData.birthTime?.substring(0, 2).trim() || ''}
                                onChange={(e) => {
                                  const h = e.target.value;
                                  const m = formData.birthTime?.substring(3, 5).padEnd(2, ' ') || '  ';
                                  const a = formData.birthTime?.substring(6, 8).padEnd(2, ' ') || '  ';
                                  handleChange({ target: { name: 'birthTime', value: `${h}:${m} ${a}` } });
                                }}
                                options={Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1).padStart(2, '0'), label: String(i + 1).padStart(2, '0') }))}
                                placeholder="HH"
                                searchable={true}
                              />
                            </div>
                            {/* Minute */}
                            <div className="flex-1 relative">
                              <CustomSelect
                                name="birthTimeMinute"
                                value={formData.birthTime?.substring(3, 5).trim() || ''}
                                onChange={(e) => {
                                  const m = e.target.value;
                                  const h = formData.birthTime?.substring(0, 2).padEnd(2, ' ') || '  ';
                                  const a = formData.birthTime?.substring(6, 8).padEnd(2, ' ') || '  ';
                                  handleChange({ target: { name: 'birthTime', value: `${h}:${m} ${a}` } });
                                }}
                                options={Array.from({ length: 60 }, (_, i) => ({ value: String(i).padStart(2, '0'), label: String(i).padStart(2, '0') }))}
                                placeholder="MM"
                                searchable={true}
                              />
                            </div>
                            {/* AM/PM */}
                            <div className="flex-1 relative">
                              <CustomSelect
                                name="birthTimeAmPm"
                                value={formData.birthTime?.substring(6, 8).trim() || ''}
                                onChange={(e) => {
                                  const a = e.target.value;
                                  const h = formData.birthTime?.substring(0, 2).padEnd(2, ' ') || '  ';
                                  const m = formData.birthTime?.substring(3, 5).padEnd(2, ' ') || '  ';
                                  handleChange({ target: { name: 'birthTime', value: `${h}:${m} ${a}` } });
                                }}
                                options={[{ value: 'AM', label: 'AM' }, { value: 'PM', label: 'PM' }]}
                                placeholder="AM/PM"
                              />
                            </div>
                          </div>
                          {renderError("birthTime")}
                        </div>
                      </div>

                      {renderInputWithIcon(
                        FaMapMarker,
                        txt.birthPlace,
                        "birthPlace",
                        "text",
                        "City of birth",
                        true
                      )}

                      <div className="bg-[rgba(255,255,255,0.03)] rounded-xl p-6">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-4">
                          {txt.physicalAttributes}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div
                            ref={(el) => (fieldRefs.current.heightFeet = el)}
                          >
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.height}
                            </label>
                            <div className="flex gap-2">
                              <input
                                type="number"
                                name="heightFeet"
                                value={formData.heightFeet}
                                onChange={handleChange}
                                placeholder="Feet"
                                className="w-1/2 p-3 border border-rose-200 dark:border-glass-border bg-transparent dark:bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                                min="4"
                                max="7"
                              />
                              <input
                                type="number"
                                name="heightInches"
                                value={formData.heightInches}
                                onChange={handleChange}
                                placeholder="Inches"
                                className="w-1/2 p-3 border border-rose-200 dark:border-glass-border bg-transparent dark:bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                                min="0"
                                max="11"
                              />
                            </div>
                            {renderError("heightFeet")}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.weight}
                            </label>
                            <input
                              type="number"
                              name="weight"
                              value={formData.weight}
                              onChange={handleChange}
                              placeholder="kg"
                              className="w-full p-3 border border-rose-200 dark:border-glass-border bg-transparent dark:bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.bodyType}
                            </label>
                            <CustomSelect
                              options={bodyTypeOptions}
                              value={formData.bodyType}
                              onChange={handleChange}
                              name="bodyType"
                              placeholder="Select"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.complexion}
                            </label>
                            <CustomSelect
                              options={complexionOptions}
                              value={formData.complexion}
                              onChange={handleChange}
                              name="complexion"
                              placeholder="Select"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                      <div className="bg-[rgba(255,255,255,0.03)] rounded-xl p-6">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-4">
                          Account Security
                        </h4>
                        {renderInputWithIcon(
                          FaPhone,
                          txt.mobile,
                          "mobile",
                          "tel",
                          "744-8539858",
                          true
                        )}

                        {/* WhatsApp Number Field - ADDED HERE */}
                        {renderInputWithIcon(
                          FaWhatsapp,
                          txt.whatsappNumber,
                          "whatsappNumber",
                          "tel",
                          "WhatsApp number",
                          true   // ⭐ THIS adds the red *
                        )}


                        {renderInputWithIcon(
                          FaEnvelope,
                          txt.email,
                          "email",
                          "email",
                          "example@email.com",
                          true
                        )}
                        {renderInputWithIcon(
                          FaLock,
                          txt.password,
                          "password",
                          "password",
                          "Create a strong password",
                          true
                        )}
                        {renderInputWithIcon(
                          FaLock,
                          txt.confirmPassword,
                          "confirmPassword",
                          "password",
                          "Confirm your password",
                          true
                        )}
                      </div>

                      {/* Profile Photo Upload - FIXED */}
                      <div ref={(el) => (fieldRefs.current.profilePhoto = el)}>
                        <label className="block text-sm font-medium text-gray-900 dark:text-white mb-4">
                          {txt.profilePhoto}
                        </label>
                        <div className="relative border-2 border-dashed border-rose-200 dark:border-glass-border rounded-xl p-8 text-center hover:border-rose-400 hover:bg-[rgba(255,46,136,0.1)] transition-all bg-[rgba(255,255,255,0.03)]">
                          {/* This div acts as clickable area */}
                          <label className="cursor-pointer block">
                            <input
                              type="file"
                              name="profilePhoto"
                              onChange={handleChange}
                              accept="image/*"
                              className="hidden" // Use hidden instead of absolute opacity-0
                            />

                            {formData.profilePhoto ? (
                              <div>
                                <img
                                  src={URL.createObjectURL(
                                    formData.profilePhoto
                                  )}
                                  alt="Preview"
                                  className="mx-auto h-48 w-48 rounded-full object-cover mb-4 border-4 border-white shadow-lg"
                                />
                                <p className="text-sm text-text-secondary font-medium">
                                  {formData.profilePhoto.name}
                                </p>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation(); // Important!
                                    setFormData({
                                      ...formData,
                                      profilePhoto: null,
                                    });
                                  }}
                                  className="mt-2 text-sm text-primary-pink hover:text-primary-pink"
                                >
                                  Change Photo
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-4">
                                  <FaCamera className="text-4xl text-text-muted" />
                                </div>
                                <p className="text-sm text-text-secondary mb-2">
                                  Click to upload a clear profile photo
                                </p>
                                <p className="text-xs text-text-muted">
                                  JPG, PNG up to 5MB
                                </p>
                              </>
                            )}
                          </label>
                        </div>
                        {renderError("profilePhoto")}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end gap-4 pt-8 border-t border-glass-border">
                    <button
                      type="button"
                      onClick={handleNext}
                      className="w-full sm:w-auto justify-center px-8 py-3 bg-gradient-to-r from-primary-grad-start to-primary-grad-end text-white font-bold rounded-xl hover:shadow-lg transition-all flex items-center gap-2 text-sm"
                    >
                      {txt.continue} <FaChevronRight />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Family & Background */}
              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-8"
                >
                  {renderSectionHeader(txt.familyInfo, txt.step2Sub, FaHome)}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Family Information */}
                    <div className="space-y-6">
                      <div className="bg-[rgba(255,255,255,0.03)] rounded-xl p-6">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-4">
                          Parents Information
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.fatherStatus}
                            </label>
                            <CustomSelect
                              options={[
                                { value: "Alive", label: "Alive" },
                                { value: "Deceased", label: "Deceased" },
                              ]}
                              value={formData.fatherStatus}
                              onChange={handleChange}
                              name="fatherStatus"
                              placeholder="Select"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.fatherOccupation}
                            </label>
                            <input
                              type="text"
                              name="fatherOccupation"
                              value={formData.fatherOccupation}
                              onChange={handleChange}
                              placeholder="Occupation"
                              className="w-full p-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.motherStatus}
                            </label>
                            <CustomSelect
                              options={[
                                { value: "Alive", label: "Alive" },
                                { value: "Deceased", label: "Deceased" },
                              ]}
                              value={formData.motherStatus}
                              onChange={handleChange}
                              name="motherStatus"
                              placeholder="Select"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.motherOccupation}
                            </label>
                            <input
                              type="text"
                              name="motherOccupation"
                              value={formData.motherOccupation}
                              onChange={handleChange}
                              placeholder="Occupation"
                              className="w-full p-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="bg-[rgba(255,255,255,0.03)] rounded-xl p-6">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-4">
                          {txt.siblings}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.brothers}
                            </label>
                            <input
                              type="number"
                              name="noOfBrothers"
                              value={formData.noOfBrothers}
                              onChange={handleChange}
                              placeholder="Number"
                              className="w-full p-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                              min="0"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.sisters}
                            </label>
                            <input
                              type="number"
                              name="noOfSisters"
                              value={formData.noOfSisters}
                              onChange={handleChange}
                              placeholder="Number"
                              className="w-full p-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                              min="0"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.familyType}
                            </label>
                            <CustomSelect
                              options={familyTypeOptions}
                              value={formData.familyType}
                              onChange={handleChange}
                              name="familyType"
                              placeholder="Select"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.familyValues}
                            </label>
                            <CustomSelect
                              options={familyValuesOptions}
                              value={formData.familyValues}
                              onChange={handleChange}
                              name="familyValues"
                              placeholder="Select"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Community Details */}
                    <div className="space-y-6">
                      <div ref={(el) => (fieldRefs.current.maritalStatus = el)}>
                        <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                          {txt.maritalStatus} <span className="text-primary-pink">*</span>
                        </label>
                        <CustomSelect
                          options={maritalOptions}
                          value={formData.maritalStatus}
                          onChange={handleChange}
                          name="maritalStatus"
                          placeholder="Select"
                          icon={FaUser}
                        />
                        {renderError("maritalStatus")}
                      </div>

                      {formData.maritalStatus !== "Never Married" &&
                        formData.maritalStatus !== "" && (
                          <div className="bg-[rgba(255,46,136,0.1)] border border-[rgba(255,46,136,0.2)] rounded-xl p-6">
                            <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-4">
                              {txt.childrenDetails}
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                                  {txt.livingWith}
                                </label>
                                <input
                                  type="number"
                                  name="childrenLivingWith"
                                  value={formData.childrenLivingWith}
                                  onChange={handleChange}
                                  placeholder="Number"
                                  className="w-full p-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                                  min="0"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                                  {txt.notLivingWith}
                                </label>
                                <input
                                  type="number"
                                  name="childrenNotLivingWith"
                                  value={formData.childrenNotLivingWith}
                                  onChange={handleChange}
                                  placeholder="Number"
                                  className="w-full p-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                                  min="0"
                                />
                              </div>
                            </div>
                          </div>
                        )}

                      <div className="bg-[rgba(255,255,255,0.03)] rounded-xl p-6">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-4">
                          {txt.communityInfo}
                        </h4>
                        <div className="space-y-4">
                          <div ref={(el) => (fieldRefs.current.religion = el)}>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.religion} <span className="text-primary-pink">*</span>
                            </label>
                            <CustomSelect
                              options={religionOptions}
                              value={formData.religion}
                              onChange={handleChange}
                              name="religion"
                              placeholder="Select Religion"
                              searchable={true}
                            />
                            {renderError("religion")}
                          </div>

                          <div ref={(el) => (fieldRefs.current.caste = el)}>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.caste} <span className="text-primary-pink">*</span>
                            </label>
                            <CustomSelect
                              options={casteOptions}
                              value={formData.caste}
                              onChange={handleChange}
                              name="caste"
                              placeholder="Select Caste"
                              disabled={!formData.religion}
                              searchable={true}
                            />
                            {renderError("caste")}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                                {txt.subcaste}
                              </label>
                              <CustomSelect
                                options={subcasteOptions}
                                value={formData.subcaste}
                                onChange={handleChange}
                                name="subcaste"
                                placeholder="Select Subcaste"
                                disabled={!formData.caste}
                                searchable={true}
                              />

                              {/* Optional: fallback text input if "Other" is selected */}
                              {formData.subcaste === "Other" && (
                                <input
                                  type="text"
                                  name="subcaste"
                                  value={
                                    formData.subcaste === "Other"
                                      ? ""
                                      : formData.subcaste
                                  }
                                  onChange={handleChange}
                                  placeholder={
                                    lang === "en"
                                      ? "Please specify"
                                      : "குறிப்பிடவும்"
                                  }
                                  className="w-full mt-2 p-3 border border-rose-200 dark:border-glass-border rounded-xl text-sm bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white"
                                />
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                                {txt.gothram}
                              </label>
                              <input
                                type="text"
                                name="gothram"
                                value={formData.gothram}
                                onChange={handleChange}
                                placeholder="Gothram"
                                className="w-full p-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.familyOrigin}
                            </label>
                            <input
                              type="text"
                              name="familyOrigin"
                              value={formData.familyOrigin}
                              onChange={handleChange}
                              placeholder="Native place"
                              className="w-full p-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row justify-between gap-4 pt-8 border-t border-glass-border">
                    <button
                      type="button"
                      onClick={handlePrev}
                      className="w-full sm:w-auto justify-center px-8 py-3 bg-gradient-to-r from-primary-grad-start to-primary-grad-end text-white font-bold rounded-xl hover:shadow-lg transition-all flex items-center gap-2 text-sm"
                    >
                      <FaChevronLeft /> {txt.back}
                    </button>
                    <button
                      type="button"
                      onClick={handleNext}
                      className="w-full sm:w-auto justify-center px-8 py-3 bg-gradient-to-r from-primary-grad-start to-primary-grad-end text-white font-bold rounded-xl hover:shadow-lg transition-all flex items-center gap-2 text-sm"
                    >
                      {txt.continue} <FaChevronRight />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Horoscope & Lifestyle */}
              {step === 3 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-8"
                >
                  {renderSectionHeader(txt.horoscopeInfo, txt.step3Sub, FaStar)}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Horoscope Details */}
                    <div className="space-y-6">
                      <div className="bg-[rgba(255,255,255,0.03)] rounded-xl p-6">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-4">
                          Astrological Details
                        </h4>
                        <div className="space-y-4">
                          <div ref={(el) => (fieldRefs.current.rasi = el)}>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.rasi} <span className="text-primary-pink">*</span>
                            </label>
                            <CustomSelect
                              options={rasiOptions}
                              value={formData.rasi}
                              onChange={handleChange}
                              name="rasi"
                              placeholder="Select Rasi"
                              searchable={true}
                            />
                            {renderError("rasi")}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div ref={(el) => (fieldRefs.current.star = el)}>
                              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                                {txt.star} <span className="text-primary-pink">*</span>
                              </label>
                              <CustomSelect
                                options={starOptions}
                                value={formData.star}
                                onChange={handleChange}
                                name="star"
                                placeholder="Select Nakshatra"
                                searchable={true}
                              />
                              {renderError("star")}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                                {txt.pada}
                              </label>
                              <CustomSelect
                                options={padaOptions}
                                value={formData.pada}
                                onChange={handleChange}
                                name="pada"
                                placeholder="Select Pada"
                                searchable={true}
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                                {txt.nadi}
                              </label>
                              <CustomSelect
                                options={nadiOptions}
                                value={formData.nadi}
                                onChange={handleChange}
                                name="nadi"
                                placeholder="Select Nadi"
                                searchable={true}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                                {txt.manglik}
                              </label>
                              <CustomSelect
                                options={[
                                  { label: "Not Specified", value: "" },
                                  { value: "Yes", label: "Yes" },
                                  { value: "No", label: "No" },
                                  { value: "Dont Know", label: "Don't Know" },
                                ]}
                                value={formData.manglik}
                                onChange={handleChange}
                                name="manglik"
                                placeholder="Select"
                                searchable={true}
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                              {txt.dosham} <span className="text-primary-pink">*</span>
                            </label>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              {["No", "Yes", "Don't Know"].map((d) => (
                                <label
                                  key={d}
                                  className={`p-3 border rounded-xl flex items-center justify-center cursor-pointer transition-all text-sm ${formData.dosham === d
                                    ? "border-primary-pink bg-[rgba(255,46,136,0.1)] text-primary-pink font-medium"
                                    : "border-rose-200 dark:border-glass-border hover:bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white"
                                    }`}
                                >
                                  <input
                                    type="radio"
                                    name="dosham"
                                    value={d}
                                    checked={formData.dosham === d}
                                    onChange={handleChange}
                                    className="hidden"
                                  />
                                  {d}
                                </label>
                              ))}
                            </div>

                            {renderError("dosham")}

                            {formData.dosham === "Yes" && (
                              <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                                  {lang === "en" ? "Select Dosham Types" : "தோஷம் வகைகளை தேர்ந்தெடுக்கவும்"}
                                </label>
                                <CustomSelect
                                  options={[
                                    { value: "Sevvai Dosham", label: "Sevvai Dosham / செவ்வாய் தோஷம்" },
                                    { value: "Rahu Dosham", label: "Rahu Dosham / ராகு தோஷம்" },
                                    { value: "Ketu Dosham", label: "Ketu Dosham / கேது தோஷம்" },
                                    { value: "Kalathra Dosham", label: "Kalathra Dosham / களத்திர தோஷம்" },
                                    { value: "Naga Dosham", label: "Naga Dosham / நாக தோஷம்" },
                                    { value: "Sarpa Dosham", label: "Sarpa Dosham / சர்ப்ப தோஷம்" },
                                    { value: "Shani Dosham", label: "Shani Dosham / சனி தோஷம்" },
                                    { value: "Guru Chandal Dosham", label: "Guru Chandal Dosham / குரு சண்டாள தோஷம்" },
                                    { value: "Pitru Dosham", label: "Pitru Dosham / பித்ரு தோஷம்" },
                                    { value: "Putra Dosham", label: "Putra Dosham / புத்திர தோஷம்" },
                                    { value: "Chandra Dosham", label: "Chandra Dosham / சந்திர தோஷம்" },
                                    { value: "Surya Dosham", label: "Surya Dosham / சூரிய தோஷம்" },
                                    { value: "Mangalya Dosham", label: "Mangalya Dosham / மாங்கல்ய தோஷம்" },
                                    { value: "Dhana Dosham", label: "Dhana Dosham / தன தோஷம்" },
                                    { value: "Kemadruma Dosham", label: "Kemadruma Dosham / கேமத்ரும தோஷம்" },
                                    { value: "Shakata Dosham", label: "Shakata Dosham / சகட தோஷம்" },
                                    { value: "Grahana Dosham", label: "Grahana Dosham / கிரகண தோஷம்" },
                                    { value: "Pretha Dosham", label: "Pretha Dosham / பிரேத தோஷம்" },
                                    { value: "Matru Dosham", label: "Matru Dosham / மாத்ரு தோஷம்" },
                                    { value: "Kuja Dosham", label: "Kuja Dosham / குஜ தோஷம்" },
                                    { value: "Chovva Dosham", label: "Chovva Dosham / சோவ்வா தோஷம்" },
                                    { value: "Papasamyam Dosham", label: "Papasamyam Dosham / பாபசாம்ய தோஷம்" },
                                    { value: "Visha Dosham", label: "Visha Dosham / விஷ தோஷம்" },
                                    { value: "Agni Dosham", label: "Agni Dosham / அக்னி தோஷம்" },
                                    { value: "Daridra Dosham", label: "Daridra Dosham / தரித்திர தோஷம்" },
                                    { value: "Vedha Dosham", label: "Vedha Dosham / வேத தோஷம்" },
                                    { value: "Yoni Dosham", label: "Yoni Dosham / யோனி தோஷம்" },
                                    { value: "Ganda Dosham", label: "Ganda Dosham / கண்ட தோஷம்" },
                                    { value: "Mrityu Dosham", label: "Mrityu Dosham / மிருத்யு தோஷம்" },
                                    { value: "Bandhana Dosham", label: "Bandhana Dosham / பந்தன தோஷம்" }
                                  ]}
                                  value={formData.doshamTypes}
                                  onChange={handleChange}
                                  name="doshamTypes"
                                  placeholder={lang === "en" ? "Select Doshams" : "தோஷங்களை தேர்ந்தெடுக்கவும்"}
                                  multiple={true}
                                  searchable={true}
                                />
                                {renderError("doshamTypes")}
                              </div>
                            )}
                          </div>

                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-900 dark:text-white mb-4">
                          {txt.jathagamPhoto}
                        </label>
                        <div className="relative border-2 border-dashed border-rose-200 dark:border-glass-border rounded-xl p-6 text-center hover:border-rose-400 hover:bg-[rgba(255,46,136,0.1)] transition-all bg-[rgba(255,255,255,0.03)]">
                          <label className="cursor-pointer block">
                            <input
                              type="file"
                              name="jathagamPhoto"
                              onChange={handleChange}
                              accept="image/*,.pdf"
                              className="hidden"
                            />
                            {formData.jathagamPhoto ? (
                              <div>
                                {/* preview logic */}
                                <p className="text-sm text-text-secondary">
                                  {formData.jathagamPhoto.name}
                                </p>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFormData({
                                      ...formData,
                                      jathagamPhoto: null,
                                    });
                                  }}
                                  className="mt-2 text-sm text-primary-pink"
                                >
                                  Remove
                                </button>
                              </div>
                            ) : (
                              <>
                                <FaUpload className="mx-auto text-4xl text-text-muted mb-3" />
                                <p className="text-sm text-text-secondary mb-2">
                                  Click to upload Jathagam/Horoscope
                                </p>
                                <p className="text-xs text-text-muted">
                                  JPG, PNG, PDF up to 10MB
                                </p>
                              </>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Lifestyle & Habits */}
                    <div className="space-y-6">
                      <div className="bg-[rgba(255,255,255,0.03)] rounded-xl p-6">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-4">
                          {txt.lifestyle}
                        </h4>
                        <div className="space-y-6">
                          <div ref={(el) => (fieldRefs.current.diet = el)}>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                              {txt.diet}
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                              {dietOptions.map((option) => (
                                <label
                                  key={option.value}
                                  className={`p-4 border rounded-xl flex items-center justify-center cursor-pointer transition-all ${formData.diet === option.value
                                    ? "border-primary-pink bg-[rgba(255,46,136,0.1)] text-primary-pink"
                                    : "border-rose-200 dark:border-glass-border hover:bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white"
                                    }`}
                                >
                                  <input
                                    type="radio"
                                    name="diet"
                                    value={option.value}
                                    checked={formData.diet === option.value}
                                    onChange={handleChange}
                                    className="hidden"
                                  />
                                  <FaUtensils
                                    className={
                                      formData.diet === option.value
                                        ? "text-primary-pink"
                                        : "text-text-muted"
                                    }
                                  />
                                  <span className="ml-2 font-medium">
                                    {option.label}
                                  </span>
                                </label>
                              ))}
                            </div>
                            {renderError("diet")}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                              {txt.smoking}
                            </label>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              {habitOptions.map((option) => (
                                <label
                                  key={option.value}
                                  className={`p-3 border rounded-xl flex items-center justify-center cursor-pointer transition-all text-sm ${formData.smoking === option.value
                                    ? "border-primary-pink bg-[rgba(255,46,136,0.1)] text-primary-pink"
                                    : "border-rose-200 dark:border-glass-border hover:bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white"
                                    }`}
                                >
                                  <input
                                    type="radio"
                                    name="smoking"
                                    value={option.value}
                                    checked={formData.smoking === option.value}
                                    onChange={handleChange}
                                    className="hidden"
                                  />
                                  {option.label}
                                </label>
                              ))}
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                              {txt.drinking}
                            </label>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              {habitOptions.map((option) => (
                                <label
                                  key={option.value}
                                  className={`p-3 border rounded-xl flex items-center justify-center cursor-pointer transition-all text-sm ${formData.drinking === option.value
                                    ? "border-primary-pink bg-[rgba(255,46,136,0.1)] text-primary-pink"
                                    : "border-rose-200 dark:border-glass-border hover:bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white"
                                    }`}
                                >
                                  <input
                                    type="radio"
                                    name="drinking"
                                    value={option.value}
                                    checked={formData.drinking === option.value}
                                    onChange={handleChange}
                                    className="hidden"
                                  />
                                  {option.label}
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-[rgba(255,255,255,0.03)] rounded-xl p-6">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-4">
                          Additional Documents
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.idProof}
                            </label>
                            <div className="border-2 border-dashed border-rose-200 dark:border-glass-border rounded-xl p-6 text-center hover:border-rose-400 hover:bg-[rgba(255,46,136,0.1)] transition-all bg-glass-card backdrop-blur-xl">
                              <label className="cursor-pointer block">
                                <input
                                  type="file"
                                  name="idProof"
                                  onChange={handleChange}
                                  accept=".jpg,.jpeg,.png,.pdf"
                                  className="hidden"
                                />
                                {formData.idProof ? (
                                  <div>
                                    <FaIdCard className="mx-auto text-5xl text-primary-pink mb-3" />
                                    <p className="text-sm text-white font-medium">
                                      {formData.idProof.name}
                                    </p>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setFormData({
                                          ...formData,
                                          idProof: null,
                                        });
                                      }}
                                      className="mt-2 text-sm text-primary-pink hover:text-primary-pink underline"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <FaIdCard className="mx-auto text-5xl text-text-muted mb-3" />
                                    <p className="text-sm text-text-secondary font-medium">
                                      Click to upload ID Proof
                                    </p>
                                    <p className="text-xs text-text-muted mt-1">
                                      Aadhaar, Passport, Driving License etc.
                                    </p>
                                  </>
                                )}
                              </label>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.horoscopePdf}
                            </label>
                            <div className="border-2 border-dashed border-rose-200 dark:border-glass-border rounded-xl p-6 text-center hover:border-rose-400 hover:bg-[rgba(255,46,136,0.1)] transition-all bg-glass-card backdrop-blur-xl">
                              <label className="cursor-pointer block">
                                <input
                                  type="file"
                                  name="horoscopePdf"
                                  onChange={handleChange}
                                  accept=".pdf"
                                  className="hidden"
                                />
                                {formData.horoscopePdf ? (
                                  <div>
                                    <FaFileAlt className="mx-auto text-5xl text-primary-pink mb-3" />
                                    <p className="text-sm text-white font-medium">
                                      {formData.horoscopePdf.name}
                                    </p>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setFormData({
                                          ...formData,
                                          horoscopePdf: null,
                                        });
                                      }}
                                      className="mt-2 text-sm text-primary-pink hover:text-primary-pink underline"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <FaFileAlt className="mx-auto text-5xl text-text-muted mb-3" />
                                    <p className="text-sm text-text-secondary font-medium">
                                      Click to upload Horoscope PDF
                                    </p>
                                    <p className="text-xs text-text-muted mt-1">
                                      Optional • PDF only
                                    </p>
                                  </>
                                )}
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row justify-between gap-4 pt-8 border-t border-glass-border">
                    <button
                      type="button"
                      onClick={handlePrev}
                      className="w-full sm:w-auto justify-center px-8 py-3 bg-gradient-to-r from-primary-grad-start to-primary-grad-end text-white font-bold rounded-xl hover:shadow-lg transition-all flex items-center gap-2 text-sm"
                    >
                      <FaChevronLeft /> {txt.back}
                    </button>
                    <button
                      type="button"
                      onClick={handleNext}
                      className="w-full sm:w-auto justify-center px-8 py-3 bg-gradient-to-r from-primary-grad-start to-primary-grad-end text-white font-bold rounded-xl hover:shadow-lg transition-all flex items-center gap-2 text-sm"
                    >
                      {txt.continue} <FaChevronRight />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step 4: Career & Final Details */}
              {step === 4 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-8"
                >
                  {renderSectionHeader(
                    txt.educationCareer,
                    txt.step4Sub,
                    FaBriefcase
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Education & Career */}
                    <div className="space-y-6">
                      <div className="bg-[rgba(255,255,255,0.03)] rounded-xl p-6">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-4">
                          Education Details
                        </h4>
                        <div className="space-y-4">
                          <div ref={(el) => (fieldRefs.current.education = el)}>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.education} <span className="text-primary-pink">*</span>
                            </label>
                            <CustomSelect
                              options={educationOptions}
                              value={formData.education}
                              onChange={handleChange}
                              name="education"
                              placeholder={loadingMasterData ? "Loading..." : "Select Highest Education"}
                              disabled={loadingMasterData}
                              icon={FaGraduationCap}
                            />
                            {renderError("education")}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.educationDetails}
                            </label>
                            <input
                              type="text"
                              name="educationDetails"
                              value={formData.educationDetails}
                              onChange={handleChange}
                              placeholder="Specialization/Field"
                              className="w-full p-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.university}
                            </label>
                            <input
                              type="text"
                              name="university"
                              value={formData.university}
                              onChange={handleChange}
                              placeholder="University/College"
                              className="w-full p-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="bg-[rgba(255,255,255,0.03)] rounded-xl p-6">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-4">
                          Career Details
                        </h4>
                        <div className="space-y-4">
                          <div
                            ref={(el) => (fieldRefs.current.occupation = el)}
                          >
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.occupation}
                            </label>
                            <CustomSelect
                              options={occupationOptions}
                              value={formData.occupation}
                              onChange={handleChange}
                              name="occupation"
                              placeholder="Select Occupation"
                              icon={FaBriefcase}
                            />
                            {renderError("occupation")}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.company}
                            </label>
                            <input
                              type="text"
                              name="company"
                              value={formData.company}
                              onChange={handleChange}
                              placeholder="Current Company"
                              className="w-full p-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.designation}
                            </label>
                            <input
                              type="text"
                              name="designation"
                              value={formData.designation}
                              onChange={handleChange}
                              placeholder="Designation/Role"
                              className="w-full p-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.income}
                            </label>
                            <CustomSelect
                              options={incomeOptions}
                              value={formData.income}
                              onChange={handleChange}
                              name="income"
                              placeholder="Select Annual Income"
                              icon={FaMoneyBillWave}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Location & Final Details */}
                    <div className="space-y-6">
                      <div className="bg-[rgba(255,255,255,0.03)] rounded-xl p-6">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-4">
                          {txt.locationInfo}
                        </h4>
                        <div className="space-y-4">
                          <div
                            ref={(el) => (fieldRefs.current.currentCity = el)}
                          >
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.currentCity} <span className="text-primary-pink">*</span>
                            </label>
                            <input
                              type="text"
                              name="currentCity"
                              value={formData.currentCity}
                              onChange={handleChange}
                              placeholder="Current City"
                              className="w-full p-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                            />
                            {renderError("currentCity")}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                                {txt.pincode}
                              </label>
                              <input
                                type="text"
                                name="pincode"
                                value={formData.pincode}
                                onChange={handleChange}
                                placeholder="Pincode"
                                className="w-full p-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                                maxLength="6"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                                {txt.state}
                              </label>
                              <input
                                type="text"
                                name="state"
                                value={formData.state}
                                onChange={handleChange}
                                placeholder="State"
                                className="w-full p-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.country}
                            </label>
                            <input
                              type="text"
                              name="country"
                              value={formData.country}
                              onChange={handleChange}
                              className="w-full p-3 border border-rose-200 dark:border-glass-border rounded-xl text-sm bg-[rgba(255,255,255,0.02)] !text-gray-900 dark:!text-white opacity-100"
                              readOnly
                            />
                          </div>
                        </div>
                      </div>

                      <div className="bg-[rgba(255,255,255,0.03)] rounded-xl p-6">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-4">
                          {txt.socialMedia}
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.linkedin}
                            </label>
                            <div className="relative">
                              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                <FaLinkedin className="text-blue-600 text-sm" />
                              </div>
                              <input
                                type="url"
                                name="linkedin"
                                value={formData.linkedin}
                                onChange={handleChange}
                                placeholder="https://linkedin.com/in/username"
                                className="w-full pl-10 pr-4 py-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              {txt.instagram}
                            </label>
                            <div className="relative">
                              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                <FaInstagram className="text-pink-600 text-sm" />
                              </div>
                              <input
                                type="url"
                                name="instagram"
                                value={formData.instagram}
                                onChange={handleChange}
                                placeholder="https://instagram.com/username"
                                className="w-full pl-10 pr-4 py-3 border border-rose-200 dark:border-glass-border bg-[rgba(255,255,255,0.03)] text-gray-900 dark:text-white rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none shadow-inner text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[rgba(255,46,136,0.1)] border border-[rgba(255,46,136,0.2)] rounded-xl p-6">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        required
                        className="w-4 h-4 text-primary-pink rounded focus:ring-primary-pink mt-1"
                      />
                      <div>
                        <p className="text-sm text-gray-800 dark:text-white font-medium mb-1">
                          I agree to the Terms & Conditions and Privacy Policy
                        </p>
                        <p className="text-xs text-text-secondary">
                          By creating an account, you agree to our terms and
                          confirm that all information provided is accurate.
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row justify-between gap-4 pt-8 border-t border-glass-border">
                    <button
                      type="button"
                      onClick={handlePrev}
                      className="w-full sm:w-auto justify-center px-8 py-3 bg-gradient-to-r from-primary-grad-start to-primary-grad-end text-white font-bold rounded-xl hover:shadow-lg transition-all flex items-center gap-2 text-sm"
                    >
                      <FaChevronLeft /> {txt.back}
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className={`w-full sm:w-auto justify-center px-10 py-3 font-bold rounded-xl transition-all flex items-center gap-3 text-sm shadow-lg
    ${loading
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-gradient-to-r from-primary-grad-start to-primary-grad-end hover:shadow-xl hover:scale-105 text-white"
                        }`}
                    >
                      {loading ? "Registering..." : (
                        <>
                          <FaHeart className="animate-pulse" /> {txt.createProfile}
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              )}
            </form>
          </div>

          <div className="text-center mt-8 text-sm text-text-secondary">
            <p>{txt.terms}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
